import { Injectable } from "@angular/core";
import { PlanningAssistantService } from "./planning-assistant.service";
import { AbsenceCalendar } from "../_models/absence.model";
import { PATimeControl } from "../_components/planning-assistant/singletons/pa-time-control";
import type { PlanningAssistantOperation } from "../_models/planning-assistant.interface";
import type { GridTechnicianInput, TwoWeekGridData } from "../welcome/capacity-bars/two-week-grid.util";
import { buildTwoWeekGridData } from "../welcome/capacity-bars/two-week-grid.util";

// Lädt Auslastungsdaten (Prozent je Techniker und Arbeitstag) für die
// Kapazitätsansichten (2-Wochen-Grid im Dashboard, Kapazitätsplanungs-Seite)
@Injectable({
  providedIn: 'root',
})
export class CapacityDataService {

  constructor(
    private planningAssistantService: PlanningAssistantService,
    private absenceCalendar: AbsenceCalendar,
  ) {
  }

  // Montag der Woche des Datums (lokal)
  startOfWeek(date: Date): Date {
    const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    monday.setDate(monday.getDate() - (monday.getDay() + 6) % 7)
    return monday
  }

  // Lädt je Techniker die Auslastung in Prozent je Arbeitstag über `weeks`
  // Wochen ab Montag: Operationen per Bulk-Request, Abwesenheiten über den
  // AbsenceCalendar, Feiertage über die regionalen Feiertagsdaten.
  async loadGridData(
    start_monday: Date,
    weeks: number,
    technicians: GridTechnicianInput[],
    now?: Date,
  ): Promise<TwoWeekGridData> {
    const end_date = new Date(start_monday)
    end_date.setDate(end_date.getDate() + weeks * 7 - 1) // Sonntag der letzten Woche
    const date_start = PATimeControl.Instance.dateToDatestring(start_monday, true, false, '-') + ' 00:00:00'
    const date_until = PATimeControl.Instance.dateToDatestring(end_date, true, false, '-') + ' 23:59:59'

    // Operationen für alle Techniker im Zeitraum (ein Bulk-Request)
    const technician_ids = technicians.map(t => t.id)
    const operations: PlanningAssistantOperation[] = technician_ids.length
      ? await new Promise<PlanningAssistantOperation[]>(resolve => {
          this.planningAssistantService.getOperationUserInfo(
            technician_ids, date_start, date_until, {exclude_material_return: true}
          ).subscribe(
            data => resolve(data.flatMap(entry => entry.operations)),
            error => {
              console.log(error)
              resolve([])
            }
          )
        })
      : []

    // Abwesenheiten im Zeitraum in die AbsenceCalendar-Map nachladen
    await this.absenceCalendar.loadAbsencesBetweenDates(
      PATimeControl.Instance.dateToDatestring(start_monday, true, false, '-'),
      PATimeControl.Instance.dateToDatestring(end_date, true, false, '-')
    )

    return buildTwoWeekGridData({
      start_date: start_monday,
      weeks,
      technicians,
      operations,
      absence_reason_for: (technician, date) => this.getDayAbsenceReason(technician, date),
      now,
    })
  }

  // Abwesenheitsgrund je Techniker und Tag (Feiertag, Urlaub, keine Schicht) oder null
  private getDayAbsenceReason(technician: GridTechnicianInput, date: Date): string | null {
    const local_day_ts = PATimeControl.Instance.dateToTimestamp(date, true, false)

    // Regionaler öffentlicher Feiertag
    if (technician.country?.toLowerCase().replaceAll(' ', '') == 'deu') {
      const holiday_state = PATimeControl.Instance.germanStatesTranslations[technician.state]
      const public_holidays = PATimeControl.Instance.getTimestampsHolidays(local_day_ts, holiday_state)
        .filter(hd => hd.type == 'public')
      if (public_holidays.length) return public_holidays[0].name
    }

    if (!technician.absence_user_id) return null

    // Eingetragene Abwesenheit
    const date_string = PATimeControl.Instance.timestampToDatestring(local_day_ts, true, '-')
    const absences = this.absenceCalendar.getLoadedUserAbsencesOnDate(technician.absence_user_id, date_string)
    if (absences.length) {
      const reason = this.absenceCalendar.reasonMap.get(absences[0].reasonId)
      return reason?.name ?? 'Abwesend'
    }

    // Keine Schicht an einem Werktag
    const absence_user = this.absenceCalendar.absenceUserMap.get(technician.absence_user_id)
    const weekday = PATimeControl.Instance.getTimeStampsWeekDay(local_day_ts)
    if (
      PATimeControl.Instance.workDays.includes(weekday) && absence_user &&
      !absence_user.hasShiftOnWeekdayWithFallback(weekday, local_day_ts)
    ) {
      return `Keine Schicht am ${PATimeControl.Instance.weekDayToGermanName(weekday, false)}`
    }

    return null
  }
}
